import DOMPurify from 'dompurify';

// Client-safe security monitoring (no server imports)
class ClientSecurityMonitor {
	private static instance: ClientSecurityMonitor;

	private constructor() {}

	static getInstance(): ClientSecurityMonitor {
		if (!ClientSecurityMonitor.instance) {
			ClientSecurityMonitor.instance = new ClientSecurityMonitor();
		}
		return ClientSecurityMonitor.instance;
	}

	monitorInnerHTMLRendering(event: any, contentInfo: any) {
		// Client-side monitoring - just log to console
		if (contentInfo.containsScript && !contentInfo.sanitized) {
			console.warn('🔒 SECURITY WARNING: Unsanitized content with scripts detected');
		}
	}

	monitorXSSAttempt(event: any, attemptInfo: any) {
		// Client-side monitoring - just log to console
		console.warn('🔒 SECURITY WARNING: XSS attempt detected', attemptInfo);
	}
}

const clientSecurityMonitor = ClientSecurityMonitor.getInstance();

// Simple static methods for backward compatibility
export const SecureSanitizer = {
	/**
	 * Sanitize HTML content
	 */
	sanitize: (content: string, options?: any): string => {
		if (!content) return '';

		const containsScript = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi.test(content);

		const sanitized = DOMPurify.sanitize(content, {
			ALLOWED_TAGS: [
				'a',
				'b',
				'blockquote',
				'code',
				'del',
				'dd',
				'dl',
				'dt',
				'em',
				'i',
				'img',
				'kbd',
				'li',
				'ol',
				'pre',
				'q',
				's',
				'strike',
				'strong',
				'sub',
				'sup',
				'u',
				'ul',
				'br',
				'p',
				'span',
				'div',
				'h1',
				'h2',
				'h3',
				'h4',
				'h5',
				'h6'
			],
			ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'id', 'target', 'rel'],
			...options
		}) as unknown as string;

		// Monitor the sanitization
		clientSecurityMonitor.monitorInnerHTMLRendering(
			{},
			{
				content: content,
				sanitized: true,
				length: content.length,
				containsScript
			}
		);

		return sanitized;
	},

	/**
	 * Sanitize text content (remove all HTML)
	 */
	sanitizeText: (text: string): string => {
		if (!text) return '';

		// Remove all HTML tags but preserve text content
		const sanitized = DOMPurify.sanitize(text, {
			ALLOWED_TAGS: [],
			ALLOWED_ATTR: []
		}) as unknown as string;

		return sanitized;
	},

	/**
	 * Sanitize URL (treat as text)
	 */
	sanitizeUrl: (url: string): string => {
		return SecureSanitizer.sanitizeText(url);
	},

	/**
	 * Check if content contains potentially dangerous elements
	 */
	isDangerous: (content: string): boolean => {
		if (!content) return false;

		const dangerousPatterns = [
			/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
			/javascript:/gi,
			/on\w+\s*=/gi,
			/data:text\/html/gi,
			/vbscript:/gi
		];

		return dangerousPatterns.some((pattern) => pattern.test(content));
	},

	/**
	 * Monitor XSS attempt
	 */
	monitorXSSAttempt: (payload: string, source: string) => {
		clientSecurityMonitor.monitorXSSAttempt(
			{},
			{
				payload,
				source,
				blocked: true
			}
		);
	}
};
